import { defineStore } from 'pinia'
import Network from '@/Network'
import { CreateSchedule } from '@/Network/Types/Requests/ScheduleRequests'
import {
  ListAllSchedulesResponse,
  ListSchedulesResponse,
  ScheduleResponse,
  PositionResponse
} from '@/Network/Types/Responses/ScheduleUnitResponses'
import { usePermissionsStore } from '@/pinia-store/usePermissionsStore'
import { SelectOption } from '@/types'
import { getApiErrorMessage } from '@/utils/apiError'
import { SharedLoader, SharedLoadOptions, sharedLoaderFor } from '@/utils/sharedLoad'

/** Cache + dedupe de GET /schedules, um por instância da store (ver `sharedLoad.ts`). */
const allSchedulesLoaders = new WeakMap<object, SharedLoader<ListSchedulesResponse>>()

/** Escala de destino do "Mover" (H8). `companyId` null = o BE não informou a empresa. */
export type MoveScheduleOption = {
  id: number
  name: string
  healthUnitId: number
  healthUnitName: string
  companyId: number | null
}

export const SCHEDULES_LOAD_ERROR = 'Não foi possível carregar as escalas'

export type ScheduleState = {
  schedulesAdminAccess: SelectOption[]
  isLoadingSelectFetch: boolean
  schedulesForPage: ListAllSchedulesResponse[]
  isLoadingSchedulesForPage: boolean
  /** Erro do load da lista de escalas (getSchedulesForPage) — null quando ok. */
  loadError: string | null

  schedules: ListSchedulesResponse | undefined
  listSchedules: {
    title: string
    value: string
    timezone?: string
    requiresDoctorPaymentFormation?: boolean
    subtitle?: string
    positions: PositionResponse[]
  }[]
  schedulesMap: Map<number, string>
  isLoading: boolean
  /** Erro da última carga de `getAllSchedules` — null quando ok. */
  allSchedulesLoadError: string | null
}

export const useScheduleStore = defineStore('schedule', {
  state: (): ScheduleState => ({
    isLoadingSelectFetch: false,
    schedulesAdminAccess: [],
    schedulesForPage: [],
    isLoadingSchedulesForPage: false,
    loadError: null,

    schedules: undefined,
    listSchedules: [],
    schedulesMap: new Map(),
    isLoading: false,
    allSchedulesLoadError: null
  }),
  actions: {
    async getSchedulesForPage() {
      try {
        this.isLoadingSchedulesForPage = true
        this.loadError = null
        const data = await Network.schedule.listAll()
        this.schedulesForPage = data
      } catch (error) {
        // Sem toast na store (legado removido): expõe o estado e relança — os
        // callers diretos exibem notifyError com o contexto de cada tela.
        this.loadError = getApiErrorMessage(error, SCHEDULES_LOAD_ERROR)
        throw error
      } finally {
        this.isLoadingSchedulesForPage = false
      }
    },
    async getScheduleOptions(adminView: boolean = false) {
      // Sem toast na store (legado removido): falha propaga para o caller exibir
      // notifyError; o finally garante o reset do loading.
      try {
        this.isLoadingSelectFetch = true
        if (adminView) {
          const response = await Network.schedule.listAdminAccess()
          this.schedulesAdminAccess = response
        }
      } finally {
        this.isLoadingSelectFetch = false
      }
    },
    /**
     * Lista completa de escalas para os selects/filtros. Chamadas simultâneas
     * compartilham a mesma request e o resultado fica em cache por 5 min no escopo
     * (token + company_ids + época de escrita): o dashboard chamava isto ~4,8 vezes
     * por carga. `force` ignora o cache. Cache quente ainda reconstrói `listSchedules`
     * completo, porque `setSchedulesByHealthUnit(s)` o sobrescreve com o recorte.
     */
    async getAllSchedules({ force = false }: SharedLoadOptions = {}): Promise<void> {
      try {
        this.isLoading = true
        this.allSchedulesLoadError = null
        const data = await sharedLoaderFor(allSchedulesLoaders, this).load(
          () => Network.schedule.list(),
          { force }
        )
        this.schedules = data
        this.listSchedules = data.data.map((schedule) => {
          this.schedulesMap.set(+schedule.id, schedule.name)
          return {
            title: schedule.name,
            value: String(schedule.id),
            timezone: schedule.timezone,
            subtitle: schedule.healthUnit?.name,
            positions: schedule.positions,
            requiresDoctorPaymentFormation: schedule.requiresDoctorPaymentFormation
          }
        })
      } catch (error) {
        // fallback intencional: store legada com ~16 callers que não esperam rejeição;
        // a falha fica em `allSchedulesLoadError` (lido pelo filtro mobile do dashboard,
        // useFilterOptions) e os selects mostram lista vazia. Sem toast na store.
        console.error('Erro ao obter schedules:', error)
        this.allSchedulesLoadError = getApiErrorMessage(error, SCHEDULES_LOAD_ERROR)
        this.schedules = { count: 0, data: [] }
        this.listSchedules = []
      } finally {
        this.isLoading = false
      }
    },
    /**
     * Derruba o cache de `getAllSchedules`. As escritas via axios já fazem isso
     * pela época de escrita; as mutações da store chamam explicitamente também.
     */
    invalidateAllSchedules(): void {
      sharedLoaderFor(allSchedulesLoaders, this).invalidate()
    },
    /**
     * Destinos do "Mover" (H8), ordenados por unidade e escala. Não mexe em `listSchedules`,
     * que é estado do filtro de escalas da barra. O filtro por `station_editing` fica no modal.
     * Erro propaga: o modal mostra a falha. Reaproveita o cache/dedupe de `getAllSchedules`.
     */
    async listScheduleOptionsForMove(): Promise<MoveScheduleOption[]> {
      const data = await sharedLoaderFor(allSchedulesLoaders, this).load(() =>
        Network.schedule.list()
      )
      // Escala sem unidade não entra: sem ela não dá para avisar o que o BE vai limpar.
      return (data?.data ?? [])
        .filter((schedule) => Number.isFinite(Number(schedule.healthUnit?.id)))
        .map((schedule) => ({
          id: Number(schedule.id),
          name: schedule.name,
          healthUnitId: Number(schedule.healthUnit.id),
          healthUnitName: schedule.healthUnit.name ?? '',
          companyId: schedule.healthUnit.company?.id ?? null
        }))
        .sort(
          (a, b) =>
            a.healthUnitName.localeCompare(b.healthUnitName, 'pt-BR') ||
            a.name.localeCompare(b.name, 'pt-BR')
        )
    },
    /**
     * Refetch pós-mutação. fallback intencional: a escrita já foi persistida —
     * rejeitar aqui faria o caller exibir um erro FALSO de criação/edição/exclusão.
     * O erro do load fica exposto em `loadError` (getSchedulesForPage seta antes
     * de relançar) para a view exibir estado visível.
     */
    async refreshSchedulesAfterMutation(): Promise<void> {
      this.invalidateAllSchedules()
      try {
        await this.getSchedulesForPage()
      } catch {
        // fallback intencional: a escrita já foi persistida (ver JSDoc acima).
        console.error('Falha ao recarregar as escalas após a mutação:', this.loadError)
      }
    },
    /**
     * Criar uma escala muda o que o usuário pode LER, e o
     * gate de render do dashboard (`canReadSchedule` em DashboardView.vue) e da
     * listagem (`ListSchedules.vue`) consulta o PermissionSet em memória — que
     * `fetchPermissions` memoiza por toda a sessão do SPA e só recarrega no
     * login. Sem este refetch, a escala recém-criada some do dashboard e da
     * própria lista até o usuário dar F5.
     *
     * Só a criação chama este refetch: no backend, `permissionsOperation` só grava
     * relação de permissão ao criar a escala. Editar não muda o que o gestor pode
     * ler, e uma escala removida sai da listagem pela própria API.
     *
     * Mesmo fallback do refetch da lista: a criação já foi persistida, então
     * uma falha aqui não pode virar erro falso de criação.
     *
     * A frescura do dado depende do backend ter propagado a permissão; ver o PR
     * irmão em bng-health/minhaescala-backend.
     */
    async refreshPermissionsAfterMutation(): Promise<void> {
      await usePermissionsStore().refreshAfterMutation()
    },
    async create({ healthUnitId, payload }: { healthUnitId: number; payload: CreateSchedule }) {
      await Network.schedule.create(healthUnitId, payload)
      await this.refreshSchedulesAfterMutation()
      await this.refreshPermissionsAfterMutation()
    },
    async remove(scheduleId: number): Promise<void> {
      await Network.schedule.remove(scheduleId)
      await this.refreshSchedulesAfterMutation()
    },
    async update({
      payload,
      scheduleId
    }: {
      scheduleId: number
      payload: CreateSchedule
    }): Promise<void> {
      await Network.schedule.update(scheduleId, payload)
      await this.refreshSchedulesAfterMutation()
    },
    async setSchedulesByHealthUnit(healthUnitId: number | null) {
      if (!healthUnitId) {
        this.listSchedules = []
        return
      }
      await this.getAllSchedules()
      const schedules = this.schedules?.data.filter((h) => h.healthUnit.id === healthUnitId) || []
      this.listSchedules = schedules.map((h) => ({
        title: h.name,
        value: String(h.id),
        timezone: h.timezone,
        positions: h.positions,
        requiresDoctorPaymentFormation: h.requiresDoctorPaymentFormation
      }))
    },
    async setSchedulesByHealthUnits(healthUnitIds: number[] | null) {
      if (!healthUnitIds?.length) {
        this.listSchedules = []
        return
      }
      await this.getAllSchedules()
      const schedules =
        this.schedules?.data.filter((h) => healthUnitIds.includes(h.healthUnit.id)) || []
      this.listSchedules = schedules.map((h) => ({
        title: h.name,
        value: String(h.id),
        timezone: h.timezone,
        subtitle: h.healthUnit.name,
        positions: h.positions,
        requiresDoctorPaymentFormation: h.requiresDoctorPaymentFormation
      }))
    },
    async getScheduleById(scheduleId: number): Promise<ScheduleResponse> {
      const data = await Network.schedule.getById(scheduleId)
      return data
    }
  }
})
